import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Button } from "../../@/components/ui/button";
import { Spinner } from "@/components/spinner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";

function getUserIdValue(user) {
  return String(
    user?.clerk_id ??
      user?.clerkId ??
      user?.user_id ??
      user?.userId ??
      user?.id ??
      ""
  ).trim();
}

function getUserLabel(user) {
  const name = `${user?.first_name ?? user?.firstName ?? ""} ${
    user?.last_name ?? user?.lastName ?? ""
  }`.trim();
  return name || user?.email || getUserIdValue(user);
}

export default function AssignSubmissionReviewerDialog({
  submissionUserId,
  submissionProjectId,
  currentReviewerId,
  open,
  onOpenChange,
  onAssigned,
}) {
  const [instructors, setInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [selectedReviewerId, setSelectedReviewerId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const { getToken } = useAuth();

  const instructorOptions = useMemo(() => {
    const opts = (Array.isArray(instructors) ? instructors : [])
      .map((u) => {
        const value = getUserIdValue(u);
        if (!value) return null;
        return { value, label: getUserLabel(u) };
      })
      .filter(Boolean);

    opts.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return opts;
  }, [instructors]);

  const fetchInstructors = useCallback(async () => {
    setLoadingInstructors(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const items = await fetchAllPaginatedItems({
        url: api_endpoint + `/user/users/role/${"Instructor"}`,
        token,
        selectItems: (data) => data?.users ?? [],
        mode: "skip",
        pageSize: 200,
        headers: reqHeaders,
      });

      setInstructors(items || []);
    } catch (error) {
      console.error("Failed to fetch instructors:", error);
      setInstructors([]);
      toast.error("Failed to load instructors");
    } finally {
      setLoadingInstructors(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!open) return;

    setSelectedReviewerId(String(currentReviewerId || "").trim());
    fetchInstructors();
  }, [open, currentReviewerId, fetchInstructors]);

  const updateReviewer = async () => {
    const resolvedUserId = String(submissionUserId || "").trim();
    const resolvedProjectId = String(submissionProjectId || "").trim();

    if (!resolvedUserId || !resolvedProjectId) {
      toast.error("Missing submission user id or project id");
      return;
    }

    if (!selectedReviewerId) {
      toast.error("Please select an instructor.");
      return;
    }

    setAssigning(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const url =
        api_endpoint +
        `/project/submission/${resolvedUserId}/${resolvedProjectId}/assigned-reviewer`;
      const body = {
        reviewer_id: selectedReviewerId,
      };

      try {
        await axios.put(url, body, reqConfig);
      } catch (err) {
        const status = err?.response?.status;
        // Some backends implement this as POST/PUT.
        if (status === 405) {
          await axios.post(url, body, reqConfig);
        } else {
          throw err;
        }
      }

      toast.success("Reviewer updated successfully.");
      onAssigned?.({
        submissionUserId: resolvedUserId,
        submissionProjectId: resolvedProjectId,
        reviewerId: selectedReviewerId,
      });
      onOpenChange?.(false);
    } catch (error) {
      console.error("Error updating reviewer:", error);
      toast.error("Failed to update reviewer. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Assign New Reviewer
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Select an instructor to review this project submission.
          </DialogDescription>
        </DialogHeader>

        {loadingInstructors ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <Select value={selectedReviewerId} onValueChange={setSelectedReviewerId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an instructor" />
              </SelectTrigger>
              <SelectContent>
                {instructorOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={assigning}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant=""
                onClick={updateReviewer}
                disabled={assigning || !selectedReviewerId}
              >
                {assigning ? <ClipLoader size={18} color="#fff" /> : "Assign"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
